const CHANNEL_NAME = 'techline-dashboard-sync';

/** Message broadcast to other tabs when a techline case status changes. */
export type DashboardSyncMessage = {
    /** The case number whose status changed. */
    caseNumber?: string;

    /** The retailer number associated with the case. */
    retailerNumber?: string;

    /** The message type discriminator. */
    type: 'CASE_STATUS_CHANGED';
};

/**
 * Broadcast a case status change to any open dashboard tabs via BroadcastChannel.
 * This notifies dashboards to refresh their case lists without requiring a page reload.
 *
 * @param params - the case identifiers to include in the broadcast
 */
export function broadcastCaseStatusChange({
    caseNumber,
    retailerNumber,
}: {
    /** The case number whose status changed. */
    caseNumber?: string;

    /** The retailer number associated with the case. */
    retailerNumber?: string;
}): void {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    channel.postMessage({
        type: 'CASE_STATUS_CHANGED',
        caseNumber,
        retailerNumber,
    } satisfies DashboardSyncMessage);

    channel.close();
}

/**
 * Subscribe to case status change broadcasts from other tabs. Returns an
 * unsubscribe function that closes the underlying BroadcastChannel.
 *
 * @param callback - invoked when a CASE_STATUS_CHANGED message is received
 * @returns a cleanup function that closes the channel
 */
export function onDashboardSyncMessage(callback: (message: DashboardSyncMessage) => void): () => void {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    channel.onmessage = (event: MessageEvent<DashboardSyncMessage>) => {
        if (event.data?.type === 'CASE_STATUS_CHANGED') {
            callback(event.data);
        }
    };

    return () => {
        channel.close();
    };
}

/**
 * Broadcast a case status change to other tabs, then close the current tab.
 * If `window.close()` is blocked by the browser, falls back to navigating
 * to the dashboard after a short delay.
 *
 * @param params - the case identifiers, navigation fallback, and optional query params
 */
export function signalDashboardAndCloseTab({
    retailerNumber,
    caseNumber,
    fallbackNavigate,
    queryParams,
}: {
    /** The retailer number associated with the case. */
    retailerNumber?: string;

    /** The case number whose status changed. */
    caseNumber?: string;

    /** Navigation function used as a fallback when `window.close()` is blocked. */
    fallbackNavigate: (path: string) => void;

    /** Additional query parameters to append to the fallback dashboard URL. */
    queryParams?: string;
}): void {
    broadcastCaseStatusChange({ caseNumber, retailerNumber });

    const params = new URLSearchParams();
    if (retailerNumber) {
        params.set('retailerNumber', retailerNumber);
    }
    if (queryParams) {
        const extra = new URLSearchParams(queryParams);
        extra.forEach((value, key) => params.set(key, value));
    }
    const dashboardUrl = `/techline/dashboard${params.toString() ? `?${params.toString()}` : ''}`;

    fallbackNavigate(dashboardUrl);
}

export function sendDashboardSyncSignal({
    retailerNumber,
    caseNumber,
}: {
    retailerNumber?: string;
    caseNumber?: string;
} = {}): void {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    channel.postMessage({
        type: 'CASE_STATUS_CHANGED',
        retailerNumber,
        caseNumber,
    } satisfies DashboardSyncMessage);

    channel.close();
}
